import type { ReactNode } from 'react'
import { ArchitecturePanel, SectionLabel } from './Architecture'
import { InfoTip } from './InfoTip'
import { Hov } from '../lib/Hov'
import { sx } from '../lib/sx'
import type { ArchitectureId } from '../demo/architecture'
import type { Flow } from '../demo/state'
import type { Vals } from '../demo/vals'

/**
 * The presenter's control surface: which architecture we are arguing, which
 * flow we are on, and the triggers that make something happen. Not product UI.
 */
export function Sidebar({
  v,
  architecture,
  onArchitectureChange,
}: {
  v: Vals
  architecture: ArchitectureId
  onArchitectureChange: (id: ArchitectureId) => void
}) {
  return (
    <aside
      className="demo-sidebar"
      style={sx(
        'flex:none;display:flex;flex-direction:column;gap:20px;min-height:0;overflow:auto;padding-bottom:4px'
      )}
    >
      <header style={sx('display:flex;flex-direction:column;gap:6px')}>
        <div style={sx('display:flex;align-items:flex-start;gap:12px')}>
          <span
            style={sx(
              'flex:1;font-size:var(--fs-3xl);font-weight:var(--fw-bold);' +
                'letter-spacing:var(--ls-sm);line-height:30px'
            )}
          >
            NordVPN Group
          </span>
          <Hov
            as="button"
            onClick={v.reset}
            title="Reset demo"
            aria-label="Reset demo"
            style={sx(
              'width:32px;height:32px;flex:none;padding:0;border-radius:var(--radius-full);' +
                'border:1px solid var(--border-primary);background:transparent;cursor:pointer;' +
                'display:flex;align-items:center;justify-content:center;' +
                'transition:all 150ms var(--ease-out)'
            )}
            hoverStyle={sx('background:var(--bg-tertiary)')}
          >
            <span
              style={sx(
                'width:16px;height:16px;background:var(--text-primary);' +
                  '-webkit-mask:url(/assets/icons/reload.svg) center/contain no-repeat;' +
                  'mask:url(/assets/icons/reload.svg) center/contain no-repeat'
              )}
            />
          </Hov>
        </div>
        <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
          Concept demo · two live phones, one shared account. Actions on one device update the
          other.
        </span>
      </header>

      <ArchitecturePanel value={architecture} onChange={onArchitectureChange} />

      <section style={sx('display:flex;flex-direction:column;gap:10px')}>
        <SectionLabel>Flows</SectionLabel>

        <div style={sx('display:flex;flex-direction:column;gap:8px')}>
          <FlowCard n={1} label="Purchase group account" v={v} onSelect={v.goFlow1} />

          <FlowCard n={2} label="Invite a member" v={v} onSelect={v.goFlow2} />

          <FlowCard n={3} label="Activate group alerts" v={v} onSelect={v.goFlow3} />

          <FlowCard n={4} label="Show group alerts" v={v} onSelect={v.goFlow4}>
            <FlowFooter>
              <TriggerBadge icon="spam-call" label="Scam call" onClick={v.blockCall} />
              <TriggerBadge icon="malicious-site" label="Bad site" onClick={v.blockSite} />
              <TriggerBadge
                icon="settings"
                label="Feature off"
                onClick={v.disableFeature}
                disabled={!v.canDisableFeature}
                hint={v.disableFeatureHint}
              />
              <TriggerBadge
                icon="security-score"
                label="DWM breach"
                onClick={v.raiseBreach}
                disabled={!v.canRaiseBreach}
                hint={v.raiseBreachHint}
              />
            </FlowFooter>
          </FlowCard>
        </div>
      </section>
    </aside>
  )
}

function FlowCard({
  n,
  label,
  v,
  onSelect,
  children,
}: {
  n: Flow
  label: string
  v: Vals
  onSelect: () => void
  children?: ReactNode
}) {
  const active = v.flow === n

  return (
    <div
      style={sx(
        `border-radius:var(--radius-md);display:flex;flex-direction:column;` +
          `border:1px solid ${active ? 'var(--bg-accent)' : 'var(--border-secondary)'};` +
          `background:${active ? 'var(--bg-accent-subtle)' : 'var(--bg-secondary)'};` +
          `transition:all 150ms var(--ease-out)`
      )}
    >
      <div style={sx('display:flex;align-items:flex-start')}>
        <button
          onClick={onSelect}
          style={sx(
            'flex:1;min-width:0;text-align:left;padding:12px 4px 12px 14px;border:none;' +
              'background:transparent;color:var(--text-primary);font-family:inherit;' +
              'cursor:pointer;display:flex;flex-direction:column;gap:2px'
          )}
        >
          <span
            style={sx(
              'font-size:var(--fs-2xs);font-weight:var(--fw-medium);color:var(--text-secondary);' +
                'letter-spacing:.04em;text-transform:uppercase'
            )}
          >
            Flow {n}
          </span>
          <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium);line-height:19px')}>
            {label}
          </span>
        </button>

        <span style={sx('padding:12px 12px 0 0;display:flex')}>
          <InfoTip text={v.flowNotes[n]} label={`What happens in Flow ${n}`} />
        </span>
      </div>
      {children}
    </div>
  )
}

/**
 * Demo triggers hanging off the bottom of a flow card. No rule or heading — the
 * badges sit close enough under the flow they belong to that labelling them
 * only added weight.
 */
function FlowFooter({ children }: { children: ReactNode }) {
  return (
    <div style={sx('padding:0 14px 12px;display:flex;flex-wrap:wrap;gap:8px')}>{children}</div>
  )
}

/**
 * A demo trigger. Reads as a badge rather than a destructive button — firing
 * one is staging a scenario, not doing something dangerous.
 *
 * A disabled badge stays visible on purpose: the point of the third trigger is
 * partly that zero knowledge cannot offer it at all, and `hint` says why.
 */
function TriggerBadge({
  icon,
  label,
  onClick,
  disabled,
  hint,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
  hint?: string
}) {
  const base =
    'height:32px;padding:0 12px;border-radius:var(--radius-full);font-family:inherit;' +
    'font-size:var(--fs-xs);font-weight:var(--fw-medium);' +
    'display:flex;align-items:center;justify-content:center;gap:6px;' +
    'transition:all 150ms var(--ease-out)'

  if (disabled) {
    return (
      <span
        title={hint}
        style={sx(
          `${base};border:1px dashed var(--border-primary);background:transparent;` +
            `color:var(--text-disabled);cursor:not-allowed`
        )}
      >
        <Glyph icon={icon} />
        {label}
      </span>
    )
  }

  return (
    <Hov
      as="button"
      onClick={onClick}
      title={hint}
      style={sx(
        `${base};border:1px solid var(--border-accent);background:var(--bg-accent-subtle);` +
          `color:var(--text-accent);cursor:pointer`
      )}
      hoverStyle={sx('background:var(--bg-accent);color:#fff')}
    >
      <Glyph icon={icon} />
      {label}
    </Hov>
  )
}

function Glyph({ icon }: { icon: string }) {
  return (
    <span
      style={sx(
        `width:14px;height:14px;flex:none;background:currentColor;` +
          `-webkit-mask:url(/assets/icons/${icon}.svg) center/contain no-repeat;` +
          `mask:url(/assets/icons/${icon}.svg) center/contain no-repeat`
      )}
    />
  )
}
